import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Card } from 'react-bootstrap';
import { Link, useSearchParams, useLocation } from 'react-router-dom';
import { getWriters, getWriterById } from "../../api";
import config from '../../config';
import { getAuthorEntriesFromItem } from '../../utils/authorContributors';
import { useLanguage } from '../../i18n/LanguageContext';
import { getLocalizedItemLabels } from '../../utils/itemLocaleDisplay';
import './SearchResults.css';

/** Gelişmiş arama rozet metni (URL anahtarı + çeviri etiketleri) */
function buildCriterionBadge(key, value, writers, t) {
  const resolveAuthorName = (authorId) => {
    const writer = writers.find((w) => w.id.toString() === String(authorId));
    return writer
      ? `${writer.name} ${writer.surname}`
      : t('searchResults.authorIdFallback').replace('{id}', String(authorId));
  };
  switch (key) {
    case 'selectedAuthor':
    case 'author':
      return `${t('articleDetail.authorLabel')} ${resolveAuthorName(value)}`;
    case 'title':
      return `${t('search.tabTitle')}: "${value}"`;
    case 'content':
      return `${t('search.tabContent')}: "${value}"`;
    case 'keywords':
      return `${t('articleDetail.keywordsTitle')}: ${value}`;
    default:
      return `${key}: ${value}`;
  }
}

const SearchResults = () => {
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const { locale, t } = useLanguage();
    const [sortBy, setSortBy] = useState('relevance');
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [writers, setWriters] = useState([]);
    /** userId -> tam profil görseli URL (ortak yazarlar dahil) */
    const [writerAvatarById, setWriterAvatarById] = useState({});
    const [selectedFilters, setSelectedFilters] = useState({
        years: [],
        authors: [],
        topics: []
    });

    // URL'den arama parametrelerini al
    const isAdvancedSearch = searchParams.get('advanced') === 'true';
    const searchQuery = searchParams.get('q');
    
    // Gelişmiş arama parametreleri
    const advancedParams = {
        author: searchParams.get('author') || searchParams.get('selectedAuthor'),
        title: searchParams.get('title'),
        content: searchParams.get('content'),
        timeRange: searchParams.get('timeRange'),
        school: searchParams.get('school'),
        type: searchParams.get('type'),
        topic: searchParams.get('topic') || searchParams.get('selectedTopic'),
        keywords: searchParams.get('keywords')
    };

    useEffect(() => {
        // console.log('🔍 SearchResults component yüklendi');
        // console.log('📍 Location state:', location.state);
        // console.log('🔗 Search params:', Object.fromEntries(searchParams));
        
        // Writers'ı yükle
        const fetchWriters = async () => {
            try {
                const response = await getWriters();
                if (response && response.data) {
                    setWriters(response.data);
                    // console.log('✅ Writers yüklendi:', response.data);
                }
            } catch (error) {
                console.error('❌ Writers yüklenirken hata:', error);
            }
        };
        
        fetchWriters();
    }, []);

    useEffect(() => {
        if (!results?.length) return;

        let cancelled = false;
        (async () => {
            const need = new Set();
            for (const r of results) {
                getAuthorEntriesFromItem(r).forEach((a) => {
                    if (a.userId) need.add(String(a.userId));
                });
            }
            const updates = {};
            for (const r of results) {
                const entries = getAuthorEntriesFromItem(r);
                const first = entries[0];
                if (first?.userId && r.profileImageUrl) {
                    const p = r.profileImageUrl;
                    updates[String(first.userId)] = String(p).startsWith('http') ? p : config.userAuthApiUrl + p;
                }
            }
            await Promise.all(
                [...need].map(async (id) => {
                    if (updates[id]) return;
                    try {
                        const res = await getWriterById(id);
                        if (res?.status && res.data?.profileImageUrl) {
                            const p = res.data.profileImageUrl;
                            updates[id] = String(p).startsWith('http') ? p : config.userAuthApiUrl + p;
                        }
                    } catch (_) {
                        /* ignore */
                    }
                })
            );
            if (!cancelled) {
                setWriterAvatarById((prev) => ({ ...prev, ...updates }));
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [results]);

    useEffect(() => {
        // Location state'den sonuçları al
        if (location.state && location.state.searchResults) {
            // console.log('✅ Advanced search sonuçları location state\'den alındı:', location.state.searchResults);
            setResults(location.state.searchResults);
            setLoading(false);
        } else {
            // console.log('⚠️ Location state\'de searchResults bulunamadı, normal arama yapılacak');
            // Normal arama logic'i burada olacak
            setResults([]);
            setLoading(false);
        }
    }, [location.state, searchParams]);

  

    const handleFilterChange = (category, value) => {
        setSelectedFilters(prev => ({
            ...prev,
            [category]: prev[category].includes(value)
                ? prev[category].filter(item => item !== value)
                : [...prev[category], value]
        }));
    };

    const getSearchTitle = () => {
        if (isAdvancedSearch) {
            const activeCount = Object.entries(advancedParams).filter(
                ([_, value]) => value && String(value).trim() !== ''
            ).length;
            return activeCount > 0 ? t('searchResults.advancedTitle') : t('searchResults.advancedTitleAll');
        }
        return t('searchResults.forQuery').replace('{query}', searchQuery || '');
    };

    const getActiveFilters = () => {
        if (!isAdvancedSearch) return null;

        const activeFilters = Object.entries(advancedParams).filter(
            ([_, value]) => value && String(value).trim() !== ''
        );

        if (activeFilters.length === 0) return null;

        return (
            <div className="active-filters-display mb-3">
                <strong>{t('searchResults.criteriaHeading')}</strong>
                <div className="d-flex flex-wrap gap-2 mt-2">
                    {activeFilters.map(([key, value]) => (
                        <span key={key} className="filter-badge">
                            {buildCriterionBadge(key, value, writers, t)}
                        </span>
                    ))}
                </div>
            </div>
        );
    };

    // Tarih formatlama fonksiyonu
    const formatDate = (dateString) => {
        if (!dateString) return '';

        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return dateString;

            const localeTag = locale === 'tr' ? 'tr-TR' : locale === 'fr' ? 'fr-FR' : 'en-GB';
            return date.toLocaleDateString(localeTag, { day: '2-digit', month: '2-digit', year: 'numeric' });
        } catch (error) {
            console.error('Tarih formatlanırken hata:', error);
            return dateString;
        }
    };

    // Arama kelimelerini vurgulama fonksiyonu
    const highlightSearchTerms = (text, searchTerms) => {
        if (!text || !searchTerms || searchTerms.length === 0) return text;
        
        let highlightedText = text;
        
        searchTerms.forEach(term => {
            if (term && term.trim()) {
                // Global case-insensitive regex oluştur
                const regex = new RegExp(`(${term.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
                highlightedText = highlightedText.replace(regex, '<mark class="search-highlight">$1</mark>');
            }
        });
        
        return highlightedText;
    };

    // Arama parametrelerinden tüm arama terimlerini topla
    const getSearchTerms = () => {
        const terms = [];
        
        if (advancedParams.title) {
            terms.push(...advancedParams.title.split(' ').filter((word) => word.length > 2));
        }
        if (advancedParams.content) {
            terms.push(...advancedParams.content.split(' ').filter((word) => word.length > 2));
        }
        if (advancedParams.keywords) {
            terms.push(...advancedParams.keywords.split(',').map((k) => k.trim()).filter((word) => word.length > 2));
        }
        if (searchQuery) {
            terms.push(...searchQuery.split(' ').filter((word) => word.length > 2));
        }
        
        // Tekrar eden terimleri kaldır ve küçült
        return [...new Set(terms.map(term => term.toLowerCase()))];
    };

    const searchTerms = getSearchTerms();

    if (loading) {
        return (
            <Container fluid className="search-results-container search-results-container--loading-main py-5">
                <Row>
                    <Col md={{ span: 8, offset: 2 }}>
                        <div className="text-center">{t('searchResults.loading')}</div>
                    </Col>
                </Row>
            </Container>
        );
    }

    if (error) {
        return (
            <Container fluid className="search-results-container search-results-container--loading-main py-5">
                <Row>
                    <Col md={{ span: 8, offset: 2 }}>
                        <div className="text-center text-danger">{error}</div>
                    </Col>
                </Row>
            </Container>
        );
    }

    return (
        <Container fluid className="search-results-container py-5">
            <Row>
                {/* Filtreler - Sol Sidebar */}
                {/* <Col md={3} className="filters-sidebar">
                    <div className="filters-section">
                        <h4 className="filters-title">Filtreler</h4>
                        
                        
                        {isAdvancedSearch && (
                            <div className="active-filters mb-4">
                                <h5>Aktif Filtreler</h5>
                                {Object.entries(advancedParams).map(([key, value]) => (
                                    value && (
                                        <div key={key} className="active-filter-badge">
                                            {key}: {value}
                                        </div>
                                    )
                                ))}
                            </div>
                        )}

                
                        <div className="filter-group">
                            <h5>Yıl</h5>
                            {filters.years.map(year => (
                                <Form.Check
                                    key={year}
                                    type="checkbox"
                                    label={year}
                                    checked={selectedFilters.years.includes(year)}
                                    onChange={() => handleFilterChange('years', year)}
                                    className="filter-checkbox"
                                />
                            ))}
                        </div>

                    
                        <div className="filter-group">
                            <h5>Yazarlar</h5>
                            {filters.authors.map(author => (
                                <Form.Check
                                    key={author}
                                    type="checkbox"
                                    label={author}
                                    checked={selectedFilters.authors.includes(author)}
                                    onChange={() => handleFilterChange('authors', author)}
                                    className="filter-checkbox"
                                />
                            ))}
                        </div>

            
                        <div className="filter-group">
                            <h5>Konular</h5>
                            {filters.topics.map(topic => (
                                <Form.Check
                                    key={topic}
                                    type="checkbox"
                                    label={topic}
                                    checked={selectedFilters.topics.includes(topic)}
                                    onChange={() => handleFilterChange('topics', topic)}
                                    className="filter-checkbox"
                                />
                            ))}
                        </div>
                    </div>
                </Col> */}

                {/* Arama Sonuçları - Ana İçerik */}
                <Col md={{ span: 8, offset: 2 }} className="search-results-main-col d-flex flex-column">
                    <div className="results-header d-flex justify-content-between align-items-center mb-4">
                        <div>
                            <h2>{getSearchTitle()}</h2>
                            <p className="text-muted">
                                {t('searchResults.resultsCount').replace('{count}', String(results.length))}
                            </p>
                        </div>
                        {/* <Form.Select 
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                            className="sort-select"
                        >
                            <option value="relevance">Alaka Düzeyi</option>
                            <option value="date">Tarih</option>
                            <option value="title">Başlık</option>
                        </Form.Select> */}
                    </div>

                    {getActiveFilters()}

                    <div className="results-list">
                        {results.length === 0 ? (
                            <div className="no-results text-center py-5">
                                <Link to="/gelismisArama" className="text-decoration-none text-red text-center">
                                    <h4>{t('searchResults.noResultsTitle')}</h4>
                                    <p>{t('searchResults.noResultsHint')}</p>
                                </Link>
                            </div>
                        ) : (
                            results.map((result) => {
                                const labels = getLocalizedItemLabels(result, locale);
                                const authorEntries = getAuthorEntriesFromItem(result);
                                const authorsWithId = authorEntries.filter((a) => a.userId);
                                return (
                                <Link 
                                    to={`/article/${result.id}`}
                                    key={result.id}
                                    className="result-link"
                                >
                                    <Card className="result-card mb-4">
                                        <Card.Body>
                                            <div className="d-flex align-items-start justify-content-between">
                                                <div className="flex-grow-1">
                                                    <Card.Title 
                                                        className="result-title"
                                                        dangerouslySetInnerHTML={{
                                                            __html: highlightSearchTerms(labels.title, searchTerms)
                                                        }}
                                                    />
                                                    
                                                    {labels.definition && (
                                                        <Card.Subtitle 
                                                            className="mb-2 text-muted"
                                                            dangerouslySetInnerHTML={{
                                                                __html: highlightSearchTerms(labels.definition, searchTerms)
                                                            }}
                                                        />
                                                    )}
                                                    
                                                    <div className="result-meta mb-3">
                                                        <div
                                                            className={`result-authors-row${
                                                                authorsWithId.length > 1 ? ' result-authors-row--multiple' : ''
                                                            }`}
                                                        >
                                                            {authorsWithId.length > 0 ? (
                                                                authorsWithId.map((author) => (
                                                                    <div
                                                                        key={`${result.id}-${author.userId}`}
                                                                        className="result-author-chip"
                                                                    >
                                                                        <img
                                                                            src={
                                                                                writerAvatarById[String(author.userId)] ||
                                                                                config.defaultProfileImageUrl
                                                                            }
                                                                            alt={(author.name || '').trim()}
                                                                            className="author-avatar"
                                                                        />
                                                                        <div
                                                                            className="result-author-chip-name"
                                                                            dangerouslySetInnerHTML={{
                                                                                __html: highlightSearchTerms(
                                                                                    (author.name || '').trim(),
                                                                                    searchTerms
                                                                                ),
                                                                            }}
                                                                        />
                                                                    </div>
                                                                ))
                                                            ) : (
                                                                <div className="result-author-chip result-author-chip--single d-flex align-items-center gap-2">
                                                                    {result.profileImageUrl ? (
                                                                        <img
                                                                            src={
                                                                                String(result.profileImageUrl).startsWith('http')
                                                                                    ? result.profileImageUrl
                                                                                    : config.userAuthApiUrl + result.profileImageUrl
                                                                            }
                                                                            alt={result.writerName}
                                                                            className="author-avatar"
                                                                        />
                                                                    ) : null}
                                                                    <span
                                                                        className="author fw-bold"
                                                                        dangerouslySetInnerHTML={{
                                                                            __html: highlightSearchTerms(result.writerName, searchTerms),
                                                                        }}
                                                                    />
                                                                </div>
                                                            )}
                                                        </div>
                                                        
                                                        {/* {result.email && (
                                                            <div className="email text-muted small">
                                                                📧 {result.email}
                                                            </div>
                                                        )} */}
                                                    </div>

                                                    {result.abstract && (
                                                        <Card.Text 
                                                            className="result-abstract"
                                                            dangerouslySetInnerHTML={{
                                                                __html: highlightSearchTerms(result.abstract.length > 300 
                                                                    ? `${result.abstract.substring(0, 900)}...` 
                                                                    : result.abstract, searchTerms)
                                                            }}
                                                        />
                                                    )}

                                                    {result.keyword && (
                                                        <div className="result-keywords">
                                                            {result.keyword.split(',').map((keyword, index) => (
                                                                <span 
                                                                    key={index} 
                                                                    className="keyword-badge"
                                                                    dangerouslySetInnerHTML={{
                                                                        __html: highlightSearchTerms(keyword.trim(), searchTerms)
                                                                    }}
                                                                />
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                                
                                                <div className="result-actions">
                                                    <small className="text-muted">{formatDate(result.releaseDate)}</small>
                                                </div>
                                            </div>
                                        </Card.Body>
                                    </Card>
                                </Link>
                            );
                            })
                        )}
                    </div>
                </Col>
            </Row>
        </Container>
    );
};

export default SearchResults; 
